import { useState, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { VERIFY_OPERATOR_PIN } from 'lib/api/mutations';

/**
 * Hook for PIN verification
 * @param {Object} options
 * @param {string} options.operatorId - The operator ID for PIN verification
 * @returns {Object} PIN verification functions and state
 */
export const usePinVerification = ({ operatorId }) => {
  const [error, setError] = useState(null);
  const [lockoutDelay, setLockoutDelay] = useState(null);

  const [
    verifyPinMutation,
    { loading: isLoading },
  ] = useMutation(VERIFY_OPERATOR_PIN, { fetchPolicy: 'no-cache' });

  /**
   * Verify an operator's PIN
   * @param {string} pin - The PIN to verify
   * @returns {Promise<Object>} Verification result
   */
  const verifyPin = useCallback(
    async (pin) => {
      if (!operatorId) {
        throw new Error('Operator ID is required');
      }

      setError(null);
      setLockoutDelay(null);

      try {
        const { data } = await verifyPinMutation({
          variables: {
            input: {
              operatorId: String(operatorId),
              pin,
            },
          },
        });

        const result = data?.verifyPinResponse;

        // Check for structured validation errors first
        if (result?.code === 'ValidationError' && result?.errors) {
          const pinError = result.errors.find((err) => {
            return err.field === 'pin';
          });
          if (pinError && pinError.message) {
            setError(pinError.message);
            throw new Error(pinError.message);
          }
          setError(result.message || 'Validation failed');
          throw new Error(result.message || 'Validation failed');
        }

        // Handle the response structure: { valid, error, delay, attempts }
        if (result?.valid === null && result?.error) {
          setError(result.error);
          throw new Error(result.error);
        }

        if (result?.delay) {
          setLockoutDelay(result.delay);
        }

        return {
          valid: result?.valid,
          error: result?.error,
          delay: result?.delay,
          attempts: result?.attempts,
        };
      } catch (err) {
        // Handle network/GraphQL errors
        if (err.networkError) {
          const { statusCode } = err.networkError;
          if (statusCode === 401) {
            setError('Authentication failed. Please login again.');
          } else if (statusCode === 403) {
            setError('Access denied.');
          } else if (statusCode === 404) {
            setError('Operator not found.');
          } else if (statusCode >= 500) {
            setError('Server error. Please try again later.');
          } else {
            setError('Network error. Please check your connection.');
          }
        } else {
          setError(err.message || 'Verification failed. Please try again.');
        }

        throw err;
      }
    },
    [operatorId, verifyPinMutation]
  );

  return {
    verifyPin,
    isLoading,
    error,
    lockoutDelay,
  };
};

export default usePinVerification;
